import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BellAlertIcon, Cog6ToothIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { SETTINGS_PERMISSIONS, SHOP_PERMISSIONS } from '@invento/shared';
import { useUIStore } from '@/state/stores/ui.store';
import { useAuthStore } from '@/state/stores/auth.store';
import { clearSessionAndCache } from '@/core/auth/clear-session';
import { authService } from '@/features/auth/api/auth.service';
import {
  ROUTE_PATHS,
  isGroupActive,
  isLinkActive,
  isRouteActive,
  visibleNavigation,
} from '@/navigation';
import {
  Sidebar as SharedSidebar,
  sidebarUserFromName,
  type SidebarNavItem,
} from '@invento/ui/components/sidebar';
import { NotificationCenterPanel } from '@/features/notifications/ui/notification-center-panel';
import { useNotificationUnreadCount } from '@/features/notifications/hooks/use-notification-center';
import {
  useSettingsAccount,
  useSettingsStores,
} from '@/features/settings/hooks/queries/settings-theme.hooks';

function notificationBadgeLabel(count: number) {
  if (count <= 0) return undefined;
  return count > 99 ? '99+' : String(count);
}

export function Sidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    sidebarOpen,
    sidebarCollapsed,
    workingBranchId,
    setSidebarOpen,
    setSidebarCollapsed,
    setWorkingBranchId,
  } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationPanelRef = useRef<HTMLDivElement | null>(null);
  const userMenuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const unreadCountQuery = useNotificationUnreadCount(Boolean(user));
  const canReadStoreSettings = Boolean(
    user?.permissions.includes(SETTINGS_PERMISSIONS.READ) &&
    user.permissions.includes(SHOP_PERMISSIONS.READ),
  );
  const accountQuery = useSettingsAccount(Boolean(user));
  const storesQuery = useSettingsStores(Boolean(user && canReadStoreSettings));
  const unreadCount = unreadCountQuery.data?.data.unreadCount ?? 0;
  const unreadBadgeLabel = notificationBadgeLabel(unreadCount);
  const unreadBadgeAriaLabel = unreadBadgeLabel
    ? t('notifications:unreadBadge', {
        count: unreadCount,
        defaultValue: '{{count}} unread notifications',
      })
    : undefined;

  const closeNotifications = useCallback(() => {
    setNotificationsOpen(false);
    requestAnimationFrame(() => userMenuTriggerRef.current?.focus());
  }, []);

  const closeNotificationsForNavigation = useCallback(() => {
    setNotificationsOpen(false);
  }, []);

  const visibleNavItems = useMemo(() => {
    if (!user) return [];
    return visibleNavigation(user.permissions);
  }, [user]);

  const sharedNavItems = useMemo<SidebarNavItem[]>(
    () =>
      visibleNavItems.map((item): SidebarNavItem => {
        if (item.type === 'group') {
          return {
            type: 'group',
            id: item.id,
            label: t(item.labelKey),
            collapsedIcon: item.collapsedIcon,
            active: isGroupActive(location.pathname, item),
            children: item.children.map((child) => ({
              type: 'link',
              path: child.path,
              label: t(child.labelKey),
              icon: child.icon,
              active: isLinkActive(location.pathname, child),
            })),
          };
        }
        return {
          type: 'link',
          path: item.path,
          label: t(item.labelKey),
          icon: item.icon,
          active: isLinkActive(location.pathname, item),
        };
      }),
    [location.pathname, t, visibleNavItems],
  );

  const sidebarUser = useMemo(() => {
    if (!user) return null;
    return {
      ...sidebarUserFromName(user.firstName, user.lastName, user.roles[0]),
      badgeAriaLabel: unreadBadgeAriaLabel,
      badgeLabel: unreadBadgeLabel,
    };
  }, [unreadBadgeAriaLabel, unreadBadgeLabel, user]);

  const workingBranchOptions = useMemo(() => {
    const options: Array<{ id: string; label: string }> = [];
    const seen = new Set<string>();
    const addOption = (id: string, label: string) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      options.push({ id, label });
    };

    accountQuery.data?.data.branchGrants.forEach((grant) => {
      addOption(grant.branchId, grant.branchName);
    });
    storesQuery.data?.data.stores.forEach((store) => {
      addOption(store.storeId, store.name);
    });

    return options;
  }, [accountQuery.data, storesQuery.data]);

  const backendWorkingBranchId =
    accountQuery.data?.data.branchGrants.find((grant) => grant.isWorkingBranch)?.branchId ??
    storesQuery.data?.data.stores.find((store) => store.isMainShop)?.storeId ??
    null;

  useEffect(() => {
    if (!user) {
      if (workingBranchId) setWorkingBranchId(null);
      return;
    }

    if (workingBranchOptions.length === 0) return;
    if (workingBranchId && workingBranchOptions.some((option) => option.id === workingBranchId)) {
      return;
    }

    const fallbackBranchId =
      (backendWorkingBranchId &&
      workingBranchOptions.some((option) => option.id === backendWorkingBranchId)
        ? backendWorkingBranchId
        : null) ?? (workingBranchOptions.length === 1 ? workingBranchOptions[0]!.id : null);

    if (workingBranchId !== fallbackBranchId) {
      setWorkingBranchId(fallbackBranchId);
    }
  }, [backendWorkingBranchId, setWorkingBranchId, user, workingBranchId, workingBranchOptions]);

  useEffect(() => {
    if (!notificationsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!notificationPanelRef.current?.contains(event.target as Node)) {
        closeNotifications();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeNotifications();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeNotifications, notificationsOpen]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const frame = requestAnimationFrame(() => {
      const closeButton = notificationPanelRef.current?.querySelector<HTMLButtonElement>(
        '[data-notification-close]',
      );
      (closeButton ?? notificationPanelRef.current?.querySelector<HTMLElement>('[role="dialog"]'))?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [notificationsOpen]);

  useEffect(() => {
    if (!user) {
      setNotificationsOpen(false);
    }
  }, [user]);

  async function handleSignOut() {
    try {
      await authService.logout();
    } catch {
      // Local logout must still proceed if the server session is already gone.
    }
    clearSessionAndCache({ queryClient });
    navigate(ROUTE_PATHS.LOGIN, { replace: true });
  }

  return (
    <>
      <SharedSidebar
        items={sharedNavItems}
        user={sidebarUser}
        mobileOpen={sidebarOpen}
        collapsed={sidebarCollapsed}
        onMobileOpenChange={setSidebarOpen}
        onCollapsedChange={setSidebarCollapsed}
        onNavigate={(path) => {
          setSidebarOpen(false);
          setNotificationsOpen(false);
          navigate(path);
        }}
        onSignOut={handleSignOut}
        utilityItems={[
          {
            id: 'notifications',
            label: t('notifications:title', 'Notifications'),
            icon: BellAlertIcon,
            active: notificationsOpen,
            badgeLabel: unreadBadgeLabel,
            onSelect: () => setNotificationsOpen(true),
          },
          {
            id: 'settings',
            label: t('navigation:settings'),
            icon: Cog6ToothIcon,
            active: isRouteActive(location.pathname, ROUTE_PATHS.SETTINGS),
            onSelect: () => {
              setNotificationsOpen(false);
              navigate(ROUTE_PATHS.SETTINGS);
            },
          },
          {
            id: 'help',
            label: t('navigation:help'),
            icon: QuestionMarkCircleIcon,
            active: isRouteActive(location.pathname, ROUTE_PATHS.HELP),
            onSelect: () => {
              setNotificationsOpen(false);
              navigate(ROUTE_PATHS.HELP);
            },
          },
        ]}
        userMenuTriggerRef={userMenuTriggerRef}
        workingBranchSelector={{
          options: workingBranchOptions,
          selectedId: workingBranchId,
          loading:
            accountQuery.isLoading ||
            (canReadStoreSettings && storesQuery.isLoading && workingBranchOptions.length === 0),
          onSelect: setWorkingBranchId,
        }}
        labels={{
          brand: 'Invento',
          primaryNavigation: 'Primary navigation',
          collapseNavigation: 'Collapse navigation',
          closeNavigation: 'Close navigation',
          openUserMenu: 'Open user menu',
          closeUserMenu: 'Close user menu',
          workingBranchLabel: t('navigation:workingFrom', 'Working from'),
          workingBranchName: t('navigation:allBranches', 'All branches'),
          workingBranchLoading: t('navigation:loadingBranches', 'Loading branches'),
          workingBranchUnavailable: t('navigation:noBranchSelected', 'No branch selected'),
          signOut: t('navigation:signOut', 'Sign out'),
        }}
      />
      {notificationsOpen ? (
        <div
          ref={notificationPanelRef}
          className="fixed bottom-4 left-3 right-3 z-[1300] max-w-[390px] lg:bottom-5 lg:left-[16.5rem] lg:right-auto lg:w-[390px]"
        >
          <NotificationCenterPanel
            onClose={closeNotifications}
            onNavigateAway={closeNotificationsForNavigation}
          />
        </div>
      ) : null}
    </>
  );
}
